import React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';
import { buttonVariants } from './KoobButton';
import { KoobMenu } from './KoobMenu';
import KoobSpinner from './KoobSpinner';
import KoobTooltip from './KoobTooltip';

type ButtonVariant = VariantProps<typeof buttonVariants>['variant'];

// Bordered variants collapse their two adjacent borders into one seam via
// -ml-px. Borderless brand fills have no border, so the chevron half draws a
// faint divider itself against the fill.
const borderedVariants: ButtonVariant[] = ['secondary', 'outline'];
const seamClass = (variant: ButtonVariant) =>
  borderedVariants.includes(variant ?? 'secondary')
    ? ''
    : 'border-l border-solid border-black/10';

type KoobButtonSplitProps = VariantProps<typeof buttonVariants> & {
  /** Primary (left) action. */
  onClick?: React.MouseEventHandler;
  /** Locks both halves. */
  disabled?: boolean;
  /** Locks the primary half only, leaving the menu reachable. */
  primaryDisabled?: boolean;
  /** Tooltip on the primary half only — typically why it is locked. */
  primaryTip?: React.ReactNode;
  /** Swaps the primary label for a spinner and locks both halves. */
  loading?: boolean;
  /** Stretches the pair to the width of its container. */
  block?: boolean;
  /** Accessible name of the chevron trigger. */
  menuLabel?: string;
  /** KoobMenuItem elements. */
  menu: React.ReactNode;
  /** Primary button label. */
  children: React.ReactNode;
  testId?: string;
  /** data-testid on the chevron trigger, so e2e can open the menu. */
  menuTestId?: string;
};

const KoobButtonSplit = ({
  variant,
  size,
  onClick,
  disabled,
  primaryDisabled = false,
  primaryTip,
  loading = false,
  block = false,
  menuLabel = 'More options',
  menu,
  children,
  testId,
  menuTestId,
}: KoobButtonSplitProps) => {
  const primaryButton = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || primaryDisabled || loading}
      aria-busy={loading || undefined}
      data-testid={testId}
      className={cn(
        buttonVariants({ variant, size }),
        'rounded-r-none',
        block && 'w-full flex-1',
        loading && 'relative',
      )}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <KoobSpinner color="currentColor" size={16} />
        </span>
      )}
      {loading ? (
        <span className="invisible contents">{children}</span>
      ) : (
        children
      )}
    </button>
  );

  return (
    <div className={cn('inline-flex', block && 'flex w-full')}>
      {/* The tip wraps this half alone: the menu stays usable, so hovering the
        chevron must not explain why the primary action is locked. The wrapper
        only appears with a tip, so the other call sites keep their markup. */}
      {primaryTip ? (
        <KoobTooltip
          tip={primaryTip}
          side="top"
          // The trigger sits between the two halves: it carries the primary
          // button's own stretching so the seam does not move.
          className={cn(block && 'flex-1')}
        >
          {primaryButton}
        </KoobTooltip>
      ) : (
        primaryButton
      )}
      <PopoverPrimitive.Root>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            aria-label={menuLabel}
            disabled={disabled || loading}
            data-testid={menuTestId}
            className={cn(
              buttonVariants({ variant, size }),
              'group -ml-px rounded-l-none px-2',
              seamClass(variant),
            )}
          >
            <i className="fa-regular fa-chevron-down text-xs transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </button>
        </PopoverPrimitive.Trigger>
        <KoobMenu>{menu}</KoobMenu>
      </PopoverPrimitive.Root>
    </div>
  );
};

export default KoobButtonSplit;
